import {
  types as sdkTypes,
  createImageVariantConfig,
  createScaledImageVariantConfig,
} from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getBundles } from '../../util/api';
import * as log from '../../util/log';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const FETCH_BUNDLES_REQUEST = 'app/BundlesPage/FETCH_BUNDLES_REQUEST';
export const FETCH_BUNDLES_SUCCESS = 'app/BundlesPage/FETCH_BUNDLES_SUCCESS';
export const FETCH_BUNDLES_ERROR = 'app/BundlesPage/FETCH_BUNDLES_ERROR';

// ================ Reducer ================ //

const initialState = {
  bundles: [],
  bundleProductIds: [],
  fetchInProgress: false,
  fetchError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_BUNDLES_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };

    case FETCH_BUNDLES_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        bundles: payload.bundles,
        bundleProductIds: payload.productIds,
      };

    case FETCH_BUNDLES_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchBundlesRequest = () => ({ type: FETCH_BUNDLES_REQUEST });
export const fetchBundlesSuccess = ({ bundles, productIds }) => ({
  type: FETCH_BUNDLES_SUCCESS,
  payload: { bundles, productIds },
});
export const fetchBundlesError = e => ({
  type: FETCH_BUNDLES_ERROR,
  payload: e,
  error: true,
});

// ================ Thunks ================ //

const toUuidRefs = ids =>
  (Array.isArray(ids) ? ids : [])
    .map(id => {
      try {
        return typeof id === 'string' && id ? new UUID(id) : null;
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean);

export const fetchAllBundles = config => (dispatch, getState, sdk) => {
  dispatch(fetchBundlesRequest());

  return getBundles()
    .then(resp => {
      const bundles = Array.isArray(resp?.data) ? resp.data : [];

      // Collect all unique product IDs across all bundles
      const productIdsSet = new Set();
      for (const b of bundles) {
        if (Array.isArray(b.products)) {
          for (const p of b.products) {
            if (typeof p?.id === 'string' && p.id) productIdsSet.add(p.id);
          }
        }
      }
      const productIds = Array.from(productIdsSet);

      if (productIds.length === 0) {
        dispatch(fetchBundlesSuccess({ bundles, productIds: [] }));
        return { bundles, productIds: [] };
      }

      const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
      } = config.layout.listingImage;
      const aspectRatio = aspectHeight / aspectWidth;

      const commonShowParams = {
        include: ['author', 'images'],
        'fields.image': [
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          'variants.listing-gallery',
          'variants.listing-gallery-2x',
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        ...createScaledImageVariantConfig('listing-gallery', 900),
        ...createScaledImageVariantConfig('listing-gallery-2x', 1800),
      };

      const showPromises = productIds.map(id =>
        sdk.listings
          .show({ id: new UUID(id), ...commonShowParams })
          .then(resp => ({ ok: true, id, resp }))
          .catch(err => ({ ok: false, id, err }))
      );

      return Promise.all(showPromises).then(results => {
        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };

        results.filter(r => r.ok).forEach(r => {
          dispatch(addMarketplaceEntities(r.resp, sanitizeConfig));
        });

        const productUuidRefs = toUuidRefs(productIds);
        dispatch(fetchBundlesSuccess({ bundles, productIds: productUuidRefs }));
        return { bundles, productIds: productUuidRefs };
      });
    })
    .catch(e => {
      log.error(e, 'bundles-page-fetch-failed');
      dispatch(fetchBundlesError(storableError(e)));
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  return dispatch(fetchAllBundles(config));
};
